import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  Skeleton,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Clock } from 'lucide-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import { taskService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';
import { Task, TaskPriority } from '@/types';
import PriorityBadge from '@/shared/components/PriorityBadge';

dayjs.extend(relativeTime);

/**
 * UpcomingDeadlinesWidget
 * Shows tasks with approaching deadlines
 * Color-coded urgency for quick scanning
 */
const UpcomingDeadlinesWidget: React.FC = () => {
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: QUERY_KEYS.upcomingTasks,
    queryFn: () => taskService.getUpcomingTasks(),
    staleTime: 60000,
  });

  const tasks = data?.data || [];

  // Sort by due date (soonest first)
  const sortedTasks = [...tasks].sort((a, b) => 
    dayjs(a.dueDate).diff(dayjs(b.dueDate))
  ).slice(0, 5);

  const getDeadlineStatus = (dueDate: string) => {
    const now = dayjs();
    const deadline = dayjs(dueDate);
    const hoursUntil = deadline.diff(now, 'hour');

    if (hoursUntil < 0) {
      return { label: 'Overdue', color: 'error' as const, urgent: true };
    }
    if (hoursUntil < 24) {
      return { label: deadline.fromNow(), color: 'error' as const, urgent: true };
    }
    if (hoursUntil < 48) {
      return { label: deadline.fromNow(), color: 'warning' as const, urgent: true };
    }
    if (hoursUntil < 72) {
      return { label: deadline.fromNow(), color: 'warning' as const, urgent: false };
    }
    return { label: deadline.format('MMM D'), color: 'default' as const, urgent: false };
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Skeleton variant="text" width={180} height={28} />
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={56} sx={{ mt: 1 }} />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Clock size={20} style={{ color: '#64748b' }} />
          <Typography variant="h6" fontWeight={600}>
            Upcoming Deadlines
          </Typography>
        </Box>

        {/* Tasks List */}
        {sortedTasks.length === 0 ? (
          <Box
            sx={{
              py: 4,
              textAlign: 'center',
              bgcolor: 'background.default',
              borderRadius: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No upcoming deadlines this week 🎉
            </Typography>
          </Box>
        ) : (
          <List disablePadding sx={{ '& > *:not(:last-child)': { mb: 1 } }}>
            {sortedTasks.map((task) => {
              const status = getDeadlineStatus(task.dueDate);

              return (
                <ListItem
                  key={task.id}
                  sx={{
                    px: 2,
                    py: 1.5,
                    bgcolor: status.urgent ? `${status.color}.50` : 'background.default',
                    borderRadius: 1,
                    cursor: 'pointer',
                    border: '1px solid',
                    borderColor: status.urgent ? `${status.color}.200` : 'transparent',
                    '&:hover': {
                      bgcolor: status.urgent ? `${status.color}.100` : 'action.hover',
                    },
                  }}
                  onClick={() => navigate(`/tasks?taskId=${task.id}`)}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {status.urgent && (
                          <AlertCircle size={14} style={{ color: status.color === 'error' ? '#dc2626' : '#d97706' }} />
                        )}
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            flex: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {task.title}
                        </Typography>
                        <PriorityBadge priority={task.priority} size="small" />
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Chip
                          size="small"
                          label={status.label}
                          color={status.color}
                          sx={{ height: 20, fontSize: '0.675rem' }}
                        />
                      </Box>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </CardContent>
    </Card>
  );
};

export default UpcomingDeadlinesWidget;
